import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import {
  Hash, Save, Search, X, Download, Activity, ScrollText, ShieldCheck,
  RefreshCw, AlertTriangle, CheckCircle2, HelpCircle, ChevronDown,
} from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast } from "sonner";

/*
 * Sprint 2026-Feb — Numbering Series page enhancement.
 *
 * Adds an "audit-lens" view alongside the existing Configuration tab:
 *   • Dashboard       — one row per series with pointer, last-issued,
 *                       next, total, last-generated-at/by/branch, health.
 *   • Audit Log       — recent voucher-number generation events with
 *                       filters (module, branch, FY).
 *   • Health Checks   — 4 read-only diagnostic buttons.
 *   • Export          — one-click Excel workbook covering everything.
 *
 * Read-only for normal admins; the maintenance-shaped controls live
 * in the existing Configuration tab (edit pointer / prefix / …), which
 * is unchanged. This page is designed to help internal + statutory
 * audits WITHOUT disturbing the numbering logic itself.
 */

const REASON_META = {
  reversed_or_cancelled: {
    label: "Reversed / Cancelled",
    color: "bg-amber-100 text-amber-900 border-amber-300",
    hint: "Voucher exists but was later Cancelled / Rejected / Reversed. Number is retained for audit continuity.",
  },
  reserved_but_unused: {
    label: "Reserved but unused",
    color: "bg-sky-100 text-sky-900 border-sky-300",
    hint: "An operator claimed this seq via manual override; the actual voucher was never posted.",
  },
  unknown_or_failed_transaction: {
    label: "Unknown / needs review",
    color: "bg-rose-100 text-rose-900 border-rose-300",
    hint: "The pointer moved past this seq but no voucher exists in this collection. Most common cause is a failed transaction (validation, FY-lock, unique-key conflict).",
  },
};

function HealthBadge({ status, details }) {
  const map = {
    healthy: {
      icon: <CheckCircle2 size={12} />,
      cls: "bg-emerald-50 text-emerald-800 border-emerald-300",
      label: "Healthy",
    },
    warn: {
      icon: <AlertTriangle size={12} />,
      cls: "bg-amber-50 text-amber-800 border-amber-300",
      label: "Attention",
    },
    unknown: {
      icon: <HelpCircle size={12} />,
      cls: "bg-slate-100 text-slate-700 border-slate-300",
      label: "Unknown",
    },
  };
  const m = map[status] || map.unknown;
  return (
    <span
      title={details || ""}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${m.cls}`}
    >
      {m.icon}{m.label}
    </span>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
        active
          ? "border-[#F97316] text-[#F97316]"
          : "border-transparent text-slate-500 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

// ─── Configuration tab (previous behaviour, preserved) ──────────────
function ConfigTab() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState({});
  const [search, setSearch] = useState("");

  const load = async () => {
    const { data } = await api.get("/numbering");
    setList(data);
    const init = {};
    data.forEach((s) => {
      init[s.id] = {
        prefix: s.prefix || "",
        separator: s.separator || "/",
        padding: s.padding || 4,
        next_number: s.next_number || 1,
        postfix_type: s.postfix_type || "financial_year",
        enabled: s.enabled !== false,
      };
    });
    setEditing(init);
  };
  useEffect(() => { load(); }, []);

  const setField = (id, k, v) =>
    setEditing({ ...editing, [id]: { ...editing[id], [k]: v } });

  async function save(s) {
    try {
      const payload = {
        ...editing[s.id],
        padding: Number(editing[s.id].padding),
        next_number: Number(editing[s.id].next_number),
      };
      await api.put(`/numbering/${s.id}`, payload);
      toast.success(`${s.label} updated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  }

  function localPreview(s) {
    const e = editing[s.id] || s;
    const prefix = (e.prefix || "").trim();
    const sep = e.separator || "/";
    const pad = Math.max(1, Math.min(10, Number(e.padding) || 4));
    const seq = String(Math.max(1, Number(e.next_number) || 1)).padStart(pad, "0");
    const parts = [];
    if (prefix) parts.push(prefix);
    parts.push(seq);
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    if (e.postfix_type === "financial_year") {
      const a = m >= 4 ? y : y - 1;
      const b = a + 1;
      parts.push(`${String(a % 100).padStart(2, "0")}-${String(b % 100).padStart(2, "0")}`);
    } else if (e.postfix_type === "calendar_year") {
      parts.push(String(y));
    }
    return parts.join(sep);
  }

  const q = search.trim().toLowerCase();
  const filtered = q
    ? list.filter((s) =>
        [s.label, s.module, s.target_field, editing[s.id]?.prefix].some((v) =>
          (v || "").toString().toLowerCase().includes(q)
        )
      )
    : list;

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 sm:max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="numbering-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by module name, target field, prefix…"
            className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"
          />
          {search && (
            <button
              type="button"
              data-testid="numbering-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              title="Clear"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500" data-testid="numbering-count">
          {`${filtered.length}${q ? ` of ${list.length}` : ""} series${q ? ` · "${search}"` : ""}`}
        </div>
      </div>

      {list.length === 0 ? (
        <EmptyState title="Loading…" />
      ) : filtered.length === 0 ? (
        <EmptyState title="No series match" hint={`No numbering series match "${search}"`} />
      ) : (
        <div className="space-y-4">
          {filtered.map((s) => (
            <Card key={s.id} className="p-5" data-testid={`series-${s.module}`}>
              <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                <div className="lg:w-56">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold mb-1 flex items-center gap-1.5">
                    <Hash size={11} /> Module
                  </div>
                  <div className="font-display text-lg font-bold text-slate-900">{s.label}</div>
                  <div className="text-xs text-slate-500">{s.target_field}</div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 flex-1">
                  <Field label="Prefix">
                    <input
                      data-testid={`prefix-${s.module}`}
                      value={editing[s.id]?.prefix ?? ""}
                      onChange={(e) => setField(s.id, "prefix", e.target.value)}
                      placeholder="RCPL/SI"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Separator">
                    <select
                      value={editing[s.id]?.separator ?? "/"}
                      onChange={(e) => setField(s.id, "separator", e.target.value)}
                      className={inputCls}
                    >
                      {["/", "-", "_", "."].map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Padding">
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={editing[s.id]?.padding ?? 4}
                      onChange={(e) => setField(s.id, "padding", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Next Number">
                    <input
                      data-testid={`next-${s.module}`}
                      type="number"
                      min={1}
                      value={editing[s.id]?.next_number ?? 1}
                      onChange={(e) => setField(s.id, "next_number", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Postfix">
                    <select
                      value={editing[s.id]?.postfix_type ?? "financial_year"}
                      onChange={(e) => setField(s.id, "postfix_type", e.target.value)}
                      className={inputCls}
                    >
                      <option value="financial_year">Financial Year</option>
                      <option value="calendar_year">Calendar Year</option>
                      <option value="none">None</option>
                    </select>
                  </Field>
                </div>

                <div className="lg:w-72">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold mb-1">
                    Live Preview
                  </div>
                  <div
                    className="bg-slate-900 text-[#F97316] font-mono font-bold px-4 py-3 text-base tracking-wider"
                    data-testid={`preview-${s.module}`}
                  >
                    {localPreview(s)}
                  </div>
                </div>

                <button
                  data-testid={`save-${s.module}`}
                  onClick={() => save(s)}
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-3 text-xs uppercase tracking-wider font-bold flex items-center gap-2 self-end"
                >
                  <Save size={13} /> Save
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card className="mt-6 p-5 bg-slate-50">
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-bold mb-2">
          How it works
        </div>
        <ul className="text-sm text-slate-700 space-y-1.5 list-disc list-inside">
          <li><strong>Auto-generate:</strong> When you leave the number field blank on any document, the next sequence is consumed atomically.</li>
          <li><strong>Manual override:</strong> Type your own number — the server will reject duplicates (409).</li>
          <li><strong>Financial Year:</strong> Indian FY (April–March). Document date drives the FY suffix.</li>
          <li><strong>Sequence continuity:</strong> The sequence is CONTINUOUS across FYs by design — do NOT reset the pointer at FY rollover. Auditors expect a monotonic seq per module.</li>
        </ul>
      </Card>
    </>
  );
}

// ─── Dashboard tab ──────────────────────────────────────────────────
function DashboardTab() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/numbering/dashboard");
      setRows(data.series || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const query = q.trim().toLowerCase();
  const filtered = (rows || []).filter((r) =>
    !query || [r.module, r.label, r.prefix, r.last_issued]
      .some((v) => (v || "").toString().toLowerCase().includes(query))
  );

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 sm:max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="dashboard-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search modules…"
            className="w-full pl-9 pr-3 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"
          />
        </div>
        <button
          data-testid="dashboard-refresh"
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider hover:bg-slate-50"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading && !rows ? (
        <EmptyState title="Loading dashboard…" />
      ) : (
        <Card className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="dashboard-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Module</Th>
                <Th>Prefix</Th>
                <Th className="text-right">Current Seq</Th>
                <Th>Last Issued</Th>
                <Th>Next</Th>
                <Th className="text-right">Total</Th>
                <Th>Last Generated</Th>
                <Th>By</Th>
                <Th>Branch</Th>
                <Th>Health</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`dashboard-row-${r.module}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 px-3">
                    <div className="font-bold text-slate-900">{r.label}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{r.module}</div>
                  </td>
                  <td className="py-2.5 px-3 font-mono text-slate-700">{r.prefix || "—"}</td>
                  <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900">{r.current_seq}</td>
                  <td className="py-2.5 px-3 font-mono text-slate-700">{r.last_issued || "—"}</td>
                  <td className="py-2.5 px-3 font-mono text-[#F97316]">{r.next_voucher}</td>
                  <td className="py-2.5 px-3 text-right tabular font-bold text-slate-900">{r.total_vouchers}</td>
                  <td className="py-2.5 px-3 text-slate-600 text-[11px]">
                    {r.last_generated_at ? new Date(r.last_generated_at).toLocaleString() : "—"}
                  </td>
                  <td className="py-2.5 px-3 text-slate-600 text-[11px]">{r.last_generated_by || "—"}</td>
                  <td className="py-2.5 px-3 text-slate-600 text-[11px]">{r.last_generated_branch || "—"}</td>
                  <td className="py-2.5 px-3">
                    <HealthBadge status={r.health} details={r.health_details} />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-6 text-center text-slate-500 text-xs">
                    No matching series
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

// ─── Audit Log tab ──────────────────────────────────────────────────
function AuditLogTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ module: "", branch_id: "", fy: "", limit: 100 });
  const [branches, setBranches] = useState([]);
  const [modules, setModules] = useState([]);

  useEffect(() => {
    api.get("/branches").then(({ data }) => setBranches(data || []));
    api.get("/numbering").then(({ data }) => {
      setModules((data || []).map((s) => ({ module: s.module, label: s.label })));
    });
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/numbering/audit-log", { params });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Module">
            <select
              data-testid="log-module"
              value={filters.module}
              onChange={(e) => setFilters({ ...filters, module: e.target.value })}
              className={inputCls}
            >
              <option value="">All modules</option>
              {modules.map((m) => (
                <option key={m.module} value={m.module}>{m.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Branch">
            <select
              data-testid="log-branch"
              value={filters.branch_id}
              onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })}
              className={inputCls}
            >
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Financial Year">
            <input
              data-testid="log-fy"
              value={filters.fy}
              onChange={(e) => setFilters({ ...filters, fy: e.target.value })}
              placeholder="e.g. 26-27"
              className={inputCls}
            />
          </Field>
          <Field label="Limit">
            <select
              data-testid="log-limit"
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value) })}
              className={inputCls}
            >
              {[50, 100, 200, 500, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
        </div>
        <div className="flex justify-end mt-3">
          <button
            data-testid="log-apply"
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold uppercase tracking-wider"
          >
            <RefreshCw size={12} /> Apply
          </button>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="log-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>Voucher Number</Th>
              <Th>Module</Th>
              <Th>Branch</Th>
              <Th>FY</Th>
              <Th>Generated By</Th>
              <Th>Generated On</Th>
              <Th>IP Address</Th>
              <Th>Doc Date</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.voucher_number}-${i}`} data-testid={`log-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2.5 px-3 font-mono text-slate-900">{r.voucher_number}</td>
                <td className="py-2.5 px-3 text-slate-700">{r.module}</td>
                <td className="py-2.5 px-3 text-slate-700">{r.branch_name || "—"}</td>
                <td className="py-2.5 px-3 font-mono text-slate-700">{r.fy || "—"}</td>
                <td className="py-2.5 px-3 text-slate-700">{r.generated_by || "—"}</td>
                <td className="py-2.5 px-3 text-slate-600 text-[11px]">
                  {r.generated_at ? new Date(r.generated_at).toLocaleString() : "—"}
                </td>
                <td className="py-2.5 px-3 font-mono text-slate-600 text-[11px]">{r.generated_ip || "—"}</td>
                <td className="py-2.5 px-3 text-slate-600 text-[11px]">{r.doc_date || "—"}</td>
                <td className="py-2.5 px-3 text-slate-600 text-[11px]">{r.status || "—"}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="py-6 text-center text-slate-500 text-xs">
                  No events match your filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
      <div className="mt-2 text-[10px] text-slate-500">
        Note: IP addresses are only shown for vouchers created after IP tracking was enabled on this
        deployment. Legacy entries display &quot;—&quot;.
      </div>
    </>
  );
}

// ─── Health Checks tab ──────────────────────────────────────────────
function HealthCheckTab() {
  const [results, setResults] = useState({}); // {check_type: result}
  const [running, setRunning] = useState({});

  async function run(check_type) {
    setRunning((r) => ({ ...r, [check_type]: true }));
    try {
      const { data } = await api.post("/numbering/health-check", { check_type });
      setResults((r) => ({ ...r, [check_type]: data }));
      toast.success(
        data.status === "clean"
          ? `${check_type} check: clean`
          : `${check_type} check: findings surfaced`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Health check failed");
    } finally {
      setRunning((r) => ({ ...r, [check_type]: false }));
    }
  }

  const checks = [
    { key: "duplicate", title: "Duplicate Numbers", desc: "Verify no voucher number appears twice inside the same collection. The DB-level unique partial index makes this a formality — but auditors like to see the check receipt." },
    { key: "missing",   title: "Missing / Skipped Numbers", desc: "Enumerate live vouchers, compare with [1..pointer-1], and CLASSIFY each gap: reversed / cancelled · reserved but unused · unknown / needs review. Missing ≠ error." },
    { key: "continuity", title: "Sequence Continuity", desc: "Per-series: does the live set equal [1..pointer-1] exactly? Reports actual min/max, live count vs expected, contiguity flag." },
    { key: "index",     title: "Unique Index Status", desc: "Confirm every numbered collection has a unique partial index on its number field. This is the ultimate audit floor against duplicates." },
  ];

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 mb-4">
        {checks.map((c) => (
          <Card key={c.key} className="p-4" data-testid={`check-card-${c.key}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="font-display font-bold text-slate-900">{c.title}</div>
                <div className="text-[11px] text-slate-600 mt-1">{c.desc}</div>
              </div>
              <button
                data-testid={`run-check-${c.key}`}
                disabled={!!running[c.key]}
                onClick={() => run(c.key)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-60 text-white text-xs font-bold uppercase tracking-wider shrink-0"
              >
                {running[c.key] ? <RefreshCw size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                {running[c.key] ? "Running…" : "Run"}
              </button>
            </div>
            {results[c.key] && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${
                      results[c.key].status === "clean"
                        ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                        : "bg-amber-50 text-amber-800 border-amber-300"
                    }`}
                  >
                    {results[c.key].status === "clean" ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}
                    {results[c.key].status.replace("_", " ")}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    ran {new Date(results[c.key].generated_at).toLocaleString()}
                  </span>
                </div>
                <div className="mt-2">
                  <CheckResultBody check_type={c.key} data={results[c.key]} />
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}

function CheckResultBody({ check_type, data }) {
  if (check_type === "duplicate") {
    if (data.status === "clean") return <div className="text-xs text-emerald-800">All voucher numbers are unique.</div>;
    return (
      <div className="text-xs">
        <div className="font-bold text-rose-800 mb-2">{data.issues_count} duplicate group(s):</div>
        <ul className="space-y-1">
          {data.findings.slice(0, 20).map((f, i) => (
            <li key={i} className="font-mono">
              <span className="text-rose-800">{f.voucher_number}</span>{" "}
              <span className="text-slate-500">× {f.count} in {f.module}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (check_type === "missing") {
    return (
      <div className="text-xs">
        <div className="font-bold text-slate-800 mb-2">
          {data.total_gaps} total gaps across all series (classified below).
        </div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {data.summary.filter((s) => s.gap_count > 0).map((s, i) => (
            <details key={i} className="border border-slate-200 rounded">
              <summary className="cursor-pointer px-2 py-1.5 bg-slate-50 flex items-center gap-2 text-[11px]">
                <ChevronDown size={12} />
                <span className="font-bold">{s.label}</span>
                <span className="text-slate-500">— {s.gap_count} gaps</span>
                {Object.entries(s.by_reason).map(([reason, n]) => (
                  <span key={reason} className={`px-1.5 py-0.5 text-[9px] border ${REASON_META[reason]?.color || "bg-slate-100 text-slate-700"}`}>
                    {REASON_META[reason]?.label || reason}: {n}
                  </span>
                ))}
              </summary>
              <div className="p-2 grid grid-cols-2 md:grid-cols-4 gap-1">
                {data.findings.filter((f) => f.module === s.module).slice(0, 40).map((f, j) => (
                  <div
                    key={j}
                    title={REASON_META[f.reason]?.hint}
                    className={`px-2 py-1 text-[10px] font-mono border ${REASON_META[f.reason]?.color || "bg-slate-100"}`}
                  >
                    {f.voucher_number_placeholder} · {REASON_META[f.reason]?.label || f.reason}
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
        {data.total_gaps === 0 && <div className="text-emerald-800">No gaps found in any series.</div>}
      </div>
    );
  }
  if (check_type === "continuity") {
    return (
      <div className="text-xs max-h-64 overflow-y-auto">
        <table className="min-w-full text-[11px]">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-1.5 px-2">Module</th>
              <th className="text-right py-1.5 px-2">Pointer</th>
              <th className="text-right py-1.5 px-2">Live</th>
              <th className="text-right py-1.5 px-2">Gaps</th>
              <th className="text-center py-1.5 px-2">Contiguous?</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-1 px-2">{r.label}</td>
                <td className="py-1 px-2 text-right font-mono">{r.pointer}</td>
                <td className="py-1 px-2 text-right font-mono">{r.live_count}</td>
                <td className="py-1 px-2 text-right font-mono">{r.gap_count}</td>
                <td className="py-1 px-2 text-center">
                  {r.contiguous ? (
                    <CheckCircle2 size={12} className="inline text-emerald-600" />
                  ) : (
                    <AlertTriangle size={12} className="inline text-amber-600" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (check_type === "index") {
    return (
      <div className="text-xs max-h-64 overflow-y-auto">
        {data.rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between border-b border-slate-100 py-1">
            <div>
              <span className="font-mono text-slate-700">{r.module}</span>{" "}
              <span className="text-slate-500 text-[10px]">({r.collection}.{r.field})</span>
            </div>
            <div>
              {r.unique_index_present ? (
                <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px]">
                  <CheckCircle2 size={11} /> unique
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-rose-700 text-[10px]">
                  <AlertTriangle size={11} /> NO unique index
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function Th({ children, className = "" }) {
  return (
    <th className={`py-2.5 px-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600 ${className}`}>
      {children}
    </th>
  );
}

// ─── Main page component ────────────────────────────────────────────
export default function Numbering() {
  const [tab, setTab] = useState("config");

  async function downloadExport() {
    try {
      const res = await api.get("/numbering/audit-export", { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 15);
      a.download = `numbering-audit-${ts}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit workbook downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <PageHeader
          eyebrow="Administration"
          title="Numbering Series"
          subtitle="Configure voucher-number series, audit the sequence, and export an auditor-ready workbook. Health-check actions are strictly read-only."
        />
        <button
          data-testid="numbering-export"
          onClick={downloadExport}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold uppercase tracking-wider"
        >
          <Download size={13} /> Export audit workbook
        </button>
      </div>

      <div className="border-b border-slate-200 mb-5">
        <TabButton testid="tab-config"    active={tab === "config"}    onClick={() => setTab("config")}>Configuration</TabButton>
        <TabButton testid="tab-dashboard" active={tab === "dashboard"} onClick={() => setTab("dashboard")}>
          <span className="inline-flex items-center gap-1.5"><Activity size={12} /> Dashboard</span>
        </TabButton>
        <TabButton testid="tab-log"       active={tab === "log"}       onClick={() => setTab("log")}>
          <span className="inline-flex items-center gap-1.5"><ScrollText size={12} /> Audit Log</span>
        </TabButton>
        <TabButton testid="tab-health"    active={tab === "health"}    onClick={() => setTab("health")}>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} /> Health Checks</span>
        </TabButton>
      </div>

      {tab === "config" && <ConfigTab />}
      {tab === "dashboard" && <DashboardTab />}
      {tab === "log" && <AuditLogTab />}
      {tab === "health" && <HealthCheckTab />}
    </div>
  );
}
